import React, { useRef, useCallback, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Box } from '@mui/material';
import { FormattingToolbar, useFormattingToolbar } from '../FormattingToolbar';
import { useAuthStore } from '../../../store/storeAuth';
import { UploadService } from '../../../service/UploadService';
import { useNotificationStore } from '../../../store/storeNotifications';

interface TinyMCERichTextInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number;
  showToolbar?: boolean;
  onVariableSuggestions?: (show: boolean, suggestions: string[], position: { x: number; y: number }, anchorElement?: HTMLElement) => void;
  suggestions?: string[];
  fieldId?: string;
}

export interface TinyMCERichTextInputRef {
  insertVariable: (variable: string) => void;
  focus: () => void;
  saveCursorPosition: () => void;
  restoreCursorPosition: () => void;
}

const TinyMCERichTextInput = forwardRef<TinyMCERichTextInputRef, TinyMCERichTextInputProps>(({
  value,
  onChange,
  placeholder = 'Type here...',
  height = 150,
  showToolbar = true,
  onVariableSuggestions,
  suggestions = [],
  fieldId = 'tinymce-field'
}, ref) => {
  const editorRef = useRef<any>(null);
  const { user } = useAuthStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);
  const [isEditorReady, setIsEditorReady] = useState(false);
  
  const {
    formatting,
    showToolbar: showFormattingToolbar,
    toolbarPosition,
    updateFormatting,
    showFormattingToolbar: showToolbarFn,
    hideFormattingToolbarForce
  } = useFormattingToolbar();

  useImperativeHandle(ref, () => ({
    insertVariable: (variable: string) => {
      insertVariable(variable);
    },
    focus: () => {
      if (editorRef.current) {
        try {
          const editor = editorRef.current;
          if (editor && typeof editor.focus === 'function') {
            editor.focus();
          }
        } catch (error) {
          console.error('Could not focus editor:', error);
        }
      }
    },
    saveCursorPosition: () => {
      saveCursorPosition();
    },
    restoreCursorPosition: () => {
      restoreCursorPosition();
    }
  }));

  const insertVariable = useCallback((variable: string) => {
    if (!editorRef.current || !isEditorReady) return;

    try {
      const editor = editorRef.current;
      
      if (!editor) {
        console.error('ERROR: No editor available');
        return;
      }
      
      const currentContent = editor.getContent({ format: 'text' });
      
      let newContent = currentContent;
      let wasReplaced = false;
      
      if (cursorContextRef.current) {
        
        const context = cursorContextRef.current;
        const { beforePartial, partialText, afterCursor } = context;
        
        
        const searchPattern = beforePartial + partialText;
        
        const patternIndex = currentContent.indexOf(searchPattern);
        if (patternIndex !== -1) {
          
          const beforePattern = currentContent.slice(0, patternIndex + beforePartial.length);
          const afterPattern = currentContent.slice(patternIndex + beforePartial.length + partialText.length);
          
          newContent = beforePattern + `{{${variable}}}` + afterPattern;
          wasReplaced = true;
          
        } else {
        }
        
        cursorContextRef.current = null;
      }
      
      if (!wasReplaced) {
        
        const patterns = [
          /\{\{[a-zA-Z]*$/,  
          /\{\{$/,          
        ];
        
        for (const pattern of patterns) {
          const match = currentContent.match(pattern);
          if (match) {
            
            newContent = currentContent.replace(pattern, `{{${variable}}}`);
            wasReplaced = true;
            
            break;
          }
        }
      }
      
      if (!wasReplaced) {
        
        const lastDoubleOpenIndex = currentContent.lastIndexOf('{{');
        if (lastDoubleOpenIndex !== -1) {
          const afterLastOpen = currentContent.slice(lastDoubleOpenIndex);
          const closingIndex = afterLastOpen.indexOf('}}');
          
          if (closingIndex === -1) {
            
            const beforeOpen = currentContent.slice(0, lastDoubleOpenIndex);
            newContent = beforeOpen + `{{${variable}}}`;
            wasReplaced = true;
            
          }
        }
      }
      
      if (wasReplaced) {
        
        editor.setContent(newContent);
        
        setTimeout(() => {
          try {
            
            const variablePattern = `{{${variable}}}`;
            const variableIndex = newContent.lastIndexOf(variablePattern);
            
            if (variableIndex !== -1) {
              const targetPosition = variableIndex + variablePattern.length;
              
              if (editor.selection && typeof editor.selection.select === 'function') {
                const body = editor.getBody();
                const allText = body.textContent || '';
        
                
                const walker = document.createTreeWalker(
                  body,
                  NodeFilter.SHOW_TEXT,
                  null
                );
                
                let currentPos = 0;
                let targetNode = null;
                let targetOffset = 0;
                
                let node;
                while (node = walker.nextNode()) {
                  const nodeLength = node.textContent?.length || 0;
                  
                  if (currentPos + nodeLength >= targetPosition) {
                    targetNode = node;
                    targetOffset = targetPosition - currentPos;
                    break;
                  }
                  
                  currentPos += nodeLength;
                }
                
                if (targetNode) {
                  const selection = editor.selection;
                  const range = editor.dom.createRng();
                  
                  const safeOffset = Math.min(targetOffset, targetNode.textContent?.length || 0);
                  
                  range.setStart(targetNode, safeOffset);
                  range.setEnd(targetNode, safeOffset);
                  
                  selection.setRng(range);
                } else {
                  editor.selection.select(body, true);
                  editor.selection.collapse(false);
                }
              } else {
                const body = editor.getBody();
                editor.selection.select(body, true);
                editor.selection.collapse(false);
              }
            }
          } catch (cursorError) {
            console.error('Could not position cursor:', cursorError);
            try {
              const body = editor.getBody();
              editor.selection.select(body, true);
              editor.selection.collapse(false);
            } catch (fallbackError) {
              console.error('Cursor fallback also failed:', fallbackError);
            }
          }
        }, 50); 
        
      } else {
        editor.insertContent(`{{${variable}}}`);
      }
      
      if (onVariableSuggestions) {
        onVariableSuggestions(false, [], { x: 0, y: 0 });
      }
      
      editor.focus();
      
      const finalContent = editor.getContent();
      let processedContent = finalContent;
      
      processedContent = processedContent.replace(/&nbsp;/g, ' ');
      
      
      onChange(processedContent);
      
      
    } catch (error) {
      console.error('ERROR in insertVariable:', error);
      try {
        editorRef.current.insertContent(`{{${variable}}}`);
      } catch (fallbackError) {
        console.error('Fallback insert also failed:', fallbackError);
      }
    }
  }, [isEditorReady, onChange, onVariableSuggestions]);



  const handleEditorChange = useCallback((content: string) => {
    onChange(content);
  }, [onChange]);

  const handleEditorBlur = useCallback(() => {
    if (!editorRef.current) return;

    try {
      const editor = editorRef.current;
      const content = editor.getContent();
      
      let processedContent = content;
      
      processedContent = processedContent.replace(
        /<span class="variable-placeholder"[^>]*data-variable="([^"]*)"[^>]*>{{[^}]*}}<\/span>/g,
        '{{$1}}'
      );
      
      processedContent = processedContent.replace(/&nbsp;/g, ' ');
      
      onChange(processedContent);
    } catch (error) {
      console.error('Could not sync content on blur:', error);
    }

    hideFormattingToolbarForce();
  }, [onChange, hideFormattingToolbarForce]);

  const processContentForEditor = useCallback((content: string) => {
    if (!content) return content;
    
    return content;
  }, []);

  const cursorContextRef = useRef<{
    bookmark: any;
    textContent: string;
    cursorPos: number;
    partialStart: number;
    partialText: string;
    beforePartial: string;
    afterCursor: string;
  } | null>(null);



  const handleKeyUp = useCallback((e: any) => {
    if (!editorRef.current || !onVariableSuggestions) return;

    try {
      const editor = editorRef.current;
      
      if (!editor || !editor.selection) return;
      
      const range = editor.selection.getRng();
      if (!range) return;
      
      const textNode = range.startContainer;
      const textContent = textNode.textContent || '';
      const cursorPos = range.startOffset;
      
      const beforeCursor = textContent.slice(0, cursorPos);
      const lastOpenBrace = beforeCursor.lastIndexOf('{{');
      const lastCloseBrace = beforeCursor.lastIndexOf('}}');
      
      if (lastOpenBrace > lastCloseBrace && lastOpenBrace !== -1) {
        const searchTerm = beforeCursor.slice(lastOpenBrace + 2);
        const filteredSuggestions = suggestions.filter(path => 
          path.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        const bookmark = editor.selection.getBookmark();
        cursorContextRef.current = {
          bookmark: bookmark,
          textContent: textContent,
          cursorPos: cursorPos,
          partialStart: lastOpenBrace,
          partialText: beforeCursor.slice(lastOpenBrace),
          beforePartial: textContent.slice(0, lastOpenBrace),
          afterCursor: textContent.slice(cursorPos)
        };
        
        
        if (filteredSuggestions.length > 0) {
          const rect = editor.selection.getRng().getBoundingClientRect();
          const editorContainer = editor.getContainer();
          onVariableSuggestions(true, filteredSuggestions, { x: rect.left, y: rect.bottom }, editorContainer);
        } else {
          onVariableSuggestions(false, [], { x: 0, y: 0 });
        }
      } else {
        cursorContextRef.current = null;
        onVariableSuggestions(false, [], { x: 0, y: 0 });
      }
    } catch (error) {
      console.error('Could not handle key up for suggestions:', error);
    }
  }, [onVariableSuggestions, suggestions]);

  const cursorPositionRef = useRef<any>(null);

  const saveCursorPosition = useCallback(() => {
    if (!editorRef.current || !isEditorReady) return;
    
    try {
      const editor = editorRef.current;
      const bookmark = editor.selection.getBookmark(2, true); 
      cursorPositionRef.current = bookmark;
    } catch (error) {
      console.error('Could not save cursor position:', error);
    }
  }, [isEditorReady]);

  const restoreCursorPosition = useCallback(() => {
    if (!editorRef.current || !isEditorReady || !cursorPositionRef.current) return;
    
    try {
      const editor = editorRef.current;
      editor.selection.moveToBookmark(cursorPositionRef.current);
      editor.focus();
    } catch (error) {
      console.error('Could not restore cursor position:', error);
      try {
        editorRef.current.focus();
      } catch (focusError) {
        console.error('Could not focus editor:', focusError);
      }
    }
  }, [isEditorReady]);

  const handleFormattingChange = useCallback((updates: any) => {
    if (!editorRef.current || !isEditorReady) return;

    try {
      const editor = editorRef.current;
      
      if (!editor || typeof editor.execCommand !== 'function') return;
      
      Object.entries(updates).forEach(([key, value]) => {
        switch (key) {
          case 'bold':
            editor.execCommand('Bold');
            break;
          case 'italic':
            editor.execCommand('Italic');
            break;
          case 'underline':
            editor.execCommand('Underline');
            break;
          case 'fontSize':
            try {
              const selection = editor.selection;
              const selectedContent = selection.getContent();
              
              if (selectedContent) {
                const styledContent = `<span style="font-size: ${value}px;">${selectedContent}</span>`;
                selection.setContent(styledContent);
                
                setTimeout(() => {
                  try {
                    const range = selection.getRng();
                    const container = range.startContainer;
                    
                    let targetSpan = null;
                    if (container.nodeType === Node.ELEMENT_NODE) {
                      targetSpan = container.querySelector('span[style*="font-size"]');
                    } else if (container.parentElement) {
                      targetSpan = container.parentElement.closest('span[style*="font-size"]');
                    }
                    
                    if (targetSpan) {
                      const newRange = editor.dom.createRng();
                      newRange.selectNodeContents(targetSpan);
                      selection.setRng(newRange);
                    }
                  } catch (restoreError) {
                    console.error('Could not restore selection:', restoreError);
                  }
                }, 10);
              } else {
                editor.formatter.register('customFontSize', {
                  inline: 'span',
                  styles: { 'font-size': `${value}px` }
                });
                
                editor.formatter.apply('customFontSize');
                
                setTimeout(() => {
                  restoreCursorPosition();
                }, 10);
                
              }
            } catch (fontSizeError) {
              console.error('Font size command failed:', fontSizeError);
              setTimeout(() => {
                restoreCursorPosition();
              }, 10);
            }
            break;
          case 'fontFamily':
            try {
              const selection = editor.selection;
              const selectedContent = selection.getContent();
              
              if (selectedContent) {
                const styledContent = `<span style="font-family: ${value}, sans-serif;">${selectedContent}</span>`;
                selection.setContent(styledContent);
                
                setTimeout(() => {
                  try {
                    const range = selection.getRng();
                    const container = range.startContainer;
                    
                    let targetSpan = null;
                    if (container.nodeType === Node.ELEMENT_NODE) {
                      targetSpan = container.querySelector('span[style*="font-family"]');
                    } else if (container.parentElement) {
                      targetSpan = container.parentElement.closest('span[style*="font-family"]');
                    }
                    
                    if (targetSpan) {
                      const newRange = editor.dom.createRng();
                      newRange.selectNodeContents(targetSpan);
                      selection.setRng(newRange);
                    }
                  } catch (restoreError) {
                    console.error('Could not restore selection:', restoreError);
                  }
                }, 10);
              } else {
                editor.formatter.register('customFontFamily', {
                  inline: 'span',
                  styles: { 'font-family': `${value}, sans-serif` }
                });
                
                editor.formatter.apply('customFontFamily');
                
                setTimeout(() => {
                  restoreCursorPosition();
                }, 10);
                
              }
            } catch (fontFamilyError) {
              console.error('Font family command failed:', fontFamilyError);
              setTimeout(() => {
                restoreCursorPosition();
              }, 10);
            }
            break;
          case 'textColor':
            editor.execCommand('ForeColor', false, value);
            break;
          case 'backgroundColor':
            editor.execCommand('BackColor', false, value);
            break;
          case 'textAlign':
            switch (value) {
              case 'left':
                editor.execCommand('JustifyLeft');
                break;
              case 'center':
                editor.execCommand('JustifyCenter');
                break;
              case 'right':
                editor.execCommand('JustifyRight');
                break;
              case 'justify':
                editor.execCommand('JustifyFull');
                break;
            }
            break;
        }
      });
      
      updateFormatting(updates);
    } catch (error) {
      console.error('Could not execute formatting command:', error);
    }
  }, [isEditorReady, updateFormatting]);

  const handleUndo = useCallback(() => {
    if (editorRef.current && isEditorReady) {
      try {
        const editor = editorRef.current;
        if (editor && typeof editor.execCommand === 'function') {
          editor.execCommand('Undo');
        }
      } catch (error) {
        console.error('Could not execute undo:', error);
      }
    }
  }, [isEditorReady]);

  const handleRedo = useCallback(() => {
    if (editorRef.current && isEditorReady) {
      try {
        const editor = editorRef.current;
        if (editor && typeof editor.execCommand === 'function') {
          editor.execCommand('Redo');
        }
      } catch (error) {
        console.error('Could not execute redo:', error);
      }
    }
  }, [isEditorReady]);

  const handleInsertPageNumber = useCallback(() => {
    if (editorRef.current && isEditorReady) {
      try {
        const editor = editorRef.current;
        if (editor && typeof editor.insertContent === 'function') {
          editor.insertContent('[pg]');
        }
      } catch (error) {
        console.error('Could not insert page number:', error);
      }
    }
  }, [isEditorReady]);

  const [isImageSelected, setIsImageSelected] = useState(false);

  const handleInsertImage = useCallback(() => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.style.display = 'none';
    
    fileInput.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      
      if (!file) return;
      
      try {
        setNotification({
          type: 'info',
          message: 'Uploading image...',
        });

        const formData = new FormData();
        formData.append('file', file);
        
        const result = await UploadService.uploadFile(formData);
        
        if (result.data && result.data.src && editorRef.current) {
          const editor = editorRef.current;
          
          const imageHtml = `<img src="${result.data.src}" alt="${file.name}" style="width: 30%; height: auto;" />`;
          
          if (isImageSelected) {
            const selection = editor.selection;
            const selectedNode = selection.getNode();
            
            if (selectedNode.nodeName === 'IMG') {
              selectedNode.src = result.data.src;
              selectedNode.alt = file.name;
            } else {
              selection.setContent(imageHtml);
            }
            
            setNotification({
              type: 'success',
              message: 'Image replaced successfully!',
            });
          } else {
            editor.insertContent(imageHtml);
            
            setNotification({
              type: 'success',
              message: 'Image inserted successfully!',
            });
          }
          
          const content = editor.getContent();
          onChange(content);
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        setNotification({
          type: 'error',
          message: 'Failed to upload image. Please try again.',
        });
      }
    };
    
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  }, [isImageSelected, onChange, setNotification]);

  const checkImageSelection = useCallback(() => {
    if (!editorRef.current || !isEditorReady) return false;

    try {
      const editor = editorRef.current;
      const selection = editor.selection;
      const selectedNode = selection.getNode();
      
      const isImg = selectedNode.nodeName === 'IMG';
      
      const selectedContent = selection.getContent();
      const hasImageInSelection = selectedContent.includes('<img');
      
      const imageSelected = isImg || hasImageInSelection;
      setIsImageSelected(imageSelected);
      return imageSelected;
    } catch (error) {
      console.error('Could not check image selection:', error);
      return false;
    }
  }, [isEditorReady]);

  useEffect(() => {
    return () => {
      if (editorRef.current && typeof editorRef.current.destroy === 'function') {
        try {
          editorRef.current.destroy();
        } catch (error) {
          console.error('Error destroying TinyMCE editor:', error);
        }
      }
    };
  }, []);

  const updateFormattingFromEditor = useCallback(() => {
    if (!editorRef.current || !isEditorReady) return;

    checkImageSelection();

    try {
      const editor = editorRef.current;
       
      if (editor && typeof editor.queryCommandState === 'function') {
        const selection = editor.selection;
        const selectedNode = selection.getNode();
        
        const getComputedStyle = (element: Element, property: string) => {
          if (element && element.nodeType === Node.ELEMENT_NODE) {
            return window.getComputedStyle(element).getPropertyValue(property);
          }
          return '';
        };
        
        const getInheritedStyle = (node: Node, property: string): string => {
          let currentNode = node;
          
          while (currentNode && currentNode.nodeType !== Node.DOCUMENT_NODE) {
            if (currentNode.nodeType === Node.ELEMENT_NODE) {
              const element = currentNode as Element;
              
              const inlineStyle = (element as HTMLElement).style?.getPropertyValue(property);
              if (inlineStyle) {
                return inlineStyle;
              }
              
              const computedStyle = getComputedStyle(element, property);
              if (computedStyle && computedStyle !== 'inherit' && computedStyle !== 'initial') {
                return computedStyle;
              }
            }
            currentNode = currentNode.parentNode as Node;
          }
          
          const body = editor.getBody();
          return getComputedStyle(body, property);
        };
        
        let fontFamily = getInheritedStyle(selectedNode, 'font-family');
        if (fontFamily) {
          const rawFont = fontFamily.split(',')[0].replace(/['"]/g, '').trim();
          
          const fontMapping: { [key: string]: string } = {
            '-apple-system': 'Arial',
            'BlinkMacSystemFont': 'Arial', 
            'Segoe UI': 'Arial',
            'system-ui': 'Arial',
            'Helvetica Neue': 'Helvetica',
            'Courier': 'Courier New',
            'Arial': 'Arial',
            'Times New Roman': 'Times New Roman',
            'Georgia': 'Georgia',
            'Verdana': 'Verdana',
            'Tahoma': 'Tahoma',
            'Trebuchet MS': 'Trebuchet MS',
            'Helvetica': 'Helvetica',
            'Courier New': 'Courier New',
            'Roboto': 'Roboto',
            'sans-serif': 'Arial',
            'serif': 'Times New Roman',
            'monospace': 'Courier New'
          };
          
          fontFamily = fontMapping[rawFont] || rawFont;
          
        }
        
        let fontSize = getInheritedStyle(selectedNode, 'font-size');
        let fontSizeNumber = 14; 
        if (fontSize) {
          const match = fontSize.match(/(\d+(?:\.\d+)?)/);
          if (match) {
            fontSizeNumber = parseInt(match[1]);
          }
        }
        
        let textColor = getInheritedStyle(selectedNode, 'color');
        if (textColor) {
          if (textColor.startsWith('rgb')) {
            const rgbMatch = textColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
            if (rgbMatch) {
              const r = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
              const g = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
              const b = parseInt(rgbMatch[3]).toString(16).padStart(2, '0');
              textColor = `#${r}${g}${b}`;
            }
          }
        }
        
        
        updateFormatting({
          bold: editor.queryCommandState('Bold'),
          italic: editor.queryCommandState('Italic'),
          underline: editor.queryCommandState('Underline'),
          textAlign: editor.queryCommandState('JustifyLeft') ? 'left' :
                    editor.queryCommandState('JustifyCenter') ? 'center' :
                    editor.queryCommandState('JustifyRight') ? 'right' : 'left',
          fontFamily: fontFamily || 'Arial',
          fontSize: fontSizeNumber.toString(),
          textColor: textColor || '#000000'
        });
      }
    } catch (error) {
      console.error('Could not update formatting from editor:', error);
    }
  }, [isEditorReady, updateFormatting, checkImageSelection]);



  return (
    <Box sx={{ position: 'relative' }}>
      <Editor
        ref={editorRef}
        apiKey="zd089kdfun2xrkm6rcxpbh2j7kum3t9yrdfi76di2wfavied"
        cloudChannel="5"
        initialValue={processContentForEditor(value)}
        onEditorChange={() => {}}
        onInit={(evt, editor) => {
          setTimeout(() => {
            editorRef.current = editor;
            setIsEditorReady(true);
          }, 100);
        }}
        onFocus={(evt, editor) => {
          if (showToolbar) {
            const editorContainer = editor.getContainer();
            showToolbarFn(fieldId, editorContainer);
          }
          setTimeout(() => {
            saveCursorPosition();
          }, 10);
          
          const activeElement = document.activeElement;
          const isFromToolbar = activeElement && (
            activeElement.closest('[role="listbox"]') || 
            activeElement.closest('.MuiMenu-root') || 
            activeElement.closest('.MuiPopover-root') || 
            activeElement.getAttribute('aria-haspopup') === 'listbox' 
          );
          
          if (!isFromToolbar) {
            setTimeout(() => {
              updateFormattingFromEditor();
              checkImageSelection();
            }, 50);
          }
        }}
        onBlur={handleEditorBlur}
        onSelectionChange={(evt, editor) => {
          updateFormattingFromEditor();
          checkImageSelection();
          setTimeout(() => {
            saveCursorPosition();
          }, 10);
        }}
        onKeyUp={handleKeyUp}
        init={{
          height,
          menubar: false,
          toolbar: false, 
          statusbar: false,
          branding: false,
          skin: user?.theme === 'dark' ? 'oxide-dark' : undefined,
          content_css: user?.theme === 'dark' ? 'dark' : undefined,
          placeholder,
          plugins: [
            'paste',
            'wordcount', 
            'noneditable'
          ],
          paste_as_text: false,
          paste_retain_style_properties: 'color font-size font-family font-weight font-style text-decoration text-align background-color',
          noneditable_class: 'variable-placeholder',
          forced_root_block: 'div',
          remove_linebreaks: false,
          content_style: `
            body { 
              font-family: Arial, sans-serif;
              font-size: 14px;
              line-height: 1.4;
              color: ${user?.theme === 'dark' ? '#D0D5DD' : '#101828'};
              background-color: ${user?.theme === 'dark' ? '#1D2939' : '#FFFFFF'};
              margin: 8px;
              padding: 0;
            }
            .variable-placeholder {
              display: inline-block;
              cursor: default;
              user-select: none;
            }
            .variable-placeholder:hover {
              background-color: ${user?.theme === 'dark' ? '#475467' : '#D0D5DD'} !important;
            }
          `,
          setup: (editor: any) => {
            editor.on('BeforeSetContent', (e: any) => {
              if (e.content) {
                e.content = processContentForEditor(e.content);
              }
            });
            
            editor.on('keydown', (e: any) => {
              const selection = editor.selection;
              const node = selection.getNode();
              
              if (node && node.classList && node.classList.contains('variable-placeholder')) {
                if (e.keyCode !== 37 && e.keyCode !== 39) { 
                  e.preventDefault();
                }
              }

              if (e.keyCode === 37 || e.keyCode === 39 || e.keyCode === 38 || e.keyCode === 40 || 
                  e.keyCode === 35 || e.keyCode === 36 || 
                  e.keyCode === 8 || e.keyCode === 46 || 
                  (e.keyCode >= 65 && e.keyCode <= 90) || 
                  (e.keyCode >= 48 && e.keyCode <= 57) || 
                  e.keyCode === 32) { 
                setTimeout(() => {
                  saveCursorPosition();
                }, 50);
              }
            });
            
            editor.on('click', (e: any) => {
              setTimeout(() => {
                saveCursorPosition();
              }, 10);
            });
            
            editor.on('keydown', (e: any) => {
              if (e.keyCode === 13) { 
                const selection = editor.selection;
                const currentNode = selection.getNode();
                
                const getCurrentFormatting = (node: Node): { fontFamily?: string; fontSize?: string; color?: string } => {
                  let currentElement = node;
                  
                  while (currentElement && currentElement.nodeType !== Node.DOCUMENT_NODE) {
                    if (currentElement.nodeType === Node.ELEMENT_NODE) {
                      const element = currentElement as HTMLElement;
                      const style = element.style;
                      
                      const formatting: any = {};
                      if (style.fontFamily) formatting.fontFamily = style.fontFamily;
                      if (style.fontSize) formatting.fontSize = style.fontSize;
                      if (style.color) formatting.color = style.color;
                      
                      if (Object.keys(formatting).length > 0) {
                        return formatting;
                      }
                    }
                    currentElement = currentElement.parentNode as Node;
                  }
                  return {};
                };
                
                const currentFormatting = getCurrentFormatting(currentNode);
                
                if (Object.keys(currentFormatting).length > 0) {
                  setTimeout(() => {
                    try {
                      const styleString = Object.entries(currentFormatting)
                        .map(([key, value]) => {
                          const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
                          return `${cssKey}: ${value}`;
                        })
                        .join('; ');
                      
                      const formattedSpan = `<br><span style="${styleString}"></span>`;
                      editor.insertContent(formattedSpan);
                      
                      const range = editor.selection.getRng();
                      if (range.endContainer && range.endContainer.previousSibling) {
                        const span = range.endContainer.previousSibling;
                        if (span.nodeType === Node.ELEMENT_NODE) {
                          range.setStart(span, 0);
                          range.setEnd(span, 0);
                          editor.selection.setRng(range);
                        }
                      }
                    } catch (error) {
                      console.error('Could not preserve formatting on new line:', error);
                    }
                  }, 10);
                }
              }
            });
          }
        }}
      />
      
      {showToolbar && showFormattingToolbar && (
        <FormattingToolbar
          show={showFormattingToolbar}
          position={toolbarPosition}
          formatting={formatting}
          onFormattingChange={handleFormattingChange}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onInsertPageNumber={handleInsertPageNumber}
          onInsertImage={handleInsertImage}
          showInsertOptions={true}
        />
      )}
    </Box>
  );
});

TinyMCERichTextInput.displayName = 'TinyMCERichTextInput';

export default TinyMCERichTextInput; 